import React, { useState, useEffect, useMemo, useRef } from 'react'
import Swal from 'sweetalert2'
import { getSession } from '../../getSession'
import { wcfInstance } from '../../axiosinstance'
import { bindUpdateFields, fetchCountries as fetchCountriesUtil, fetchSourceValues as fetchSourceValuesUtil } from '../../utils/lead'

const UpdateForm = ({
  onClose = () => { },
  fieldOptions = [],
  selectedCount = 0,
  selectedLeadIds = [],
  hashedIds = '',
  massOpBatchId = '',
  applyOnAllLeads = false,
  registerSubmitHandler = null,
}) => {
  const [field, setField] = useState('')
  const [value, setValue] = useState('')
  const [entries, setEntries] = useState([])
  const [schedule, setSchedule] = useState(false)
  const [scheduleAt, setScheduleAt] = useState('')
  const [error, setError] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  const [fetchedFieldOptions, setFetchedFieldOptions] = useState([])
  const defaultFieldOptions = fieldOptions.length ? fieldOptions : fetchedFieldOptions

  const [countryOptions, setCountryOptions] = useState([])
  const [stateOptions, setStateOptions] = useState([])
  const [cityOptions, setCityOptions] = useState([])
  const [sourceOptions, setSourceOptions] = useState([])
  const [selectedCountry, setSelectedCountry] = useState('')
  const [selectedState, setSelectedState] = useState('')

  const { userId } = getSession()
  const _fieldsLoadedRef = useRef(false)

  // Register the submit handler with the parent if the prop is provided
  useEffect(() => {
    if (typeof registerSubmitHandler === 'function') {
      registerSubmitHandler(() => handleUpdate);
    }
  }, [registerSubmitHandler, entries, schedule, scheduleAt, massOpBatchId, hashedIds, applyOnAllLeads]);

  // --- load field options ---
  useEffect(() => {
    if (_fieldsLoadedRef.current) return
    if (fieldOptions && fieldOptions.length) { _fieldsLoadedRef.current = true; return }
    _fieldsLoadedRef.current = true
    bindUpdateFields({ tableName: 'UserLeads', scheduledatetimeforlead: '', UserId: userId })
      .then(setFetchedFieldOptions)
      .catch(err => console.error('BindUpdateField load error', err))
  }, [fieldOptions, userId])

  // --- load countries ---
  useEffect(() => {
    fetchCountriesUtil().then(setCountryOptions).catch(() => { })
  }, [])

  // --- load source/medium/campaign values ---
  useEffect(() => {
    const sel = defaultFieldOptions.find(o => String(o.key) === String(field))
    const key = sel ? String(sel.key) : ''
    if (key === 'SourceName' || key === 'MediumName' || key === 'CampaignName') {
      fetchSourceValuesUtil({ type: key, UserId: userId }).then(setSourceOptions).catch(() => { })
    }
  }, [field, defaultFieldOptions, userId])

  // --- fetch states / cities ---
  const fetchStates = async (countryId) => {
    if (!countryId) return
    try {
      const resp = await wcfInstance.post('/UserCRMCampaign/LeadDetails.aspx/fill_state', { c_code: countryId })
      let list = resp?.data ?? resp
      if (typeof list === 'string') list = JSON.parse(list)
      if (Array.isArray(list)) setStateOptions(list.map(s => ({ value: s.Value, label: s.Text })))
    } catch (e) { console.warn(e) }
  }

  const fetchCities = async (stateId) => {
    if (!stateId) return
    try {
      const resp = await wcfInstance.post('/UserCRMCampaign/LeadDetails.aspx/fill_city', { stateid: stateId })
      let list = resp?.data ?? resp
      if (typeof list === 'string') list = JSON.parse(list)
      if (Array.isArray(list)) setCityOptions(list.map(c => ({ value: c.Value, label: c.Text })))
    } catch (e) { console.warn(e) }
  }

  // --- add entry ---
  const handleAdd = () => {
    setError('')
    if (!field) { setError('Please select a field'); return }
    if (!String(value || '').trim()) { setError('Please enter a value'); return }
    if (entries.some(e => e.field === field)) { setError('This field is already added'); return }

    const key = String(field)
    const optionLabel = (() => {
      if (key === 'Country') return (countryOptions.find(c => String(c.value) === String(value)) || {}).label || value
      if (key === 'State') return (stateOptions.find(s => String(s.value) === String(value)) || {}).label || value
      if (key === 'City') return (cityOptions.find(c => String(c.value) === String(value)) || {}).label || value
      if (key === 'SourceName' || key === 'MediumName' || key === 'CampaignName')
        return (sourceOptions.find(s => String(s.value) === String(value)) || {}).label || value
      return value
    })()

    setEntries(prev => [...prev, { field, value, label: optionLabel }])
    setField('')
    setValue('')
  }

  const handleRemove = (idx) => setEntries(prev => prev.filter((_, i) => i !== idx))

  // --- SUBMIT — plain async function, reads state directly at call time ---
  const handleUpdate = async () => {
    setError('')

    if (entries.length === 0) {
      setError('Add at least one field to update')
      return
    }
    if (schedule && !scheduleAt) {
      setError('Select schedule date and time')
      return
    }

    const numericBatchId = Number(massOpBatchId) || 0
    const idsFromSelection = (
      hashedIds ||
      (Array.isArray(selectedLeadIds) ? selectedLeadIds.map(id => String(id)).filter(Boolean).join('/') : '') ||
      ''
    ).trim()
    const shouldUseBatchId = !!applyOnAllLeads && numericBatchId > 0
    const idsForPayload = shouldUseBatchId ? '' : idsFromSelection

    if (!idsForPayload && !shouldUseBatchId) {
      setError('No leads selected.')
      return
    }

    const commaSeparatedHashedIds = idsForPayload ? idsForPayload.replace(/\//g, ',') : ''

    const Updatedata = entries.map((e, idx) => ({
      Rowid: idx + 1,
      fieldID: e.field,
      fieldName: (defaultFieldOptions.find(f => String(f.key) === String(e.field))?.label) || e.field,
      tableName: '',
      fieldType: '',
      fieldValue: e.value,
      ids: commaSeparatedHashedIds,
      ScheduleForUpdate: schedule ? scheduleAt : ''
    }))

    const params = {
      hashedIds: shouldUseBatchId ? '' : commaSeparatedHashedIds,
      batchid: shouldUseBatchId ? numericBatchId : 0,
      Scheduledatetimelead: schedule ? scheduleAt : '',
      Updatejson: JSON.stringify(Updatedata),
      Module: 'Lead',
      UserId: userId
    }

    try {
      setIsSubmitting(true)
      const resp = await wcfInstance.post('UserCRMCampaign/Service/CRMService.asmx/MassUpdateLeadEnquiry', params)
      const body = resp?.data ?? resp
      // WCF usually returns result in 'd' property
      const message = body?.d ?? body?.Message ?? body?.Details ?? 'Updated successfully.'
      
      await Swal.fire({ 
        icon: 'success', 
        title: 'Saved', 
        text: typeof message === 'string' ? message : 'Updated successfully.' 
      })
      
      onClose()
      window.location.reload()
    } catch (err) {
      console.error('Mass update error', err)
      const errMsg = err?.response?.data?.Message || err?.message || 'Failed to save updates.'
      Swal.fire({ icon: 'error', title: 'Error', text: errMsg })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="w-full max-w-2xl flex flex-col">

      {/* header info */}
      <div className="mb-2 text-sm text-gray-600">
        {useMemo(() => {
          if (applyOnAllLeads) return `Apply on all selected leads${massOpBatchId ? ` (Batch ID: ${massOpBatchId})` : ''}`
          return `${Number(selectedCount) || 0} selected lead(s)${hashedIds ? ` (Hashed IDs: ${hashedIds})` : ''}`
        }, [applyOnAllLeads, selectedCount, massOpBatchId, hashedIds])}
      </div>

      {/* field selector */}
      <div className="mb-3">
        <select
          value={field}
          onChange={e => { setField(e.target.value); setValue(''); setSelectedCountry(''); setSelectedState('') }}
          className="w-full border px-3 py-2 rounded mb-2"
        >
          <option value="">Please Select Field</option>
          {defaultFieldOptions.map(opt => (
            <option key={opt.key} value={opt.key}>{opt.label}</option>
          ))}
        </select>

        {/* dynamic value input */}
        {(() => {
          const sel = defaultFieldOptions.find(o => String(o.key) === String(field))
          const key = sel ? String(sel.key) : ''

          if (key === 'SourceName' || key === 'MediumName' || key === 'CampaignName') {
            return (
              <select value={value} onChange={e => setValue(e.target.value)} className="w-full border px-3 py-2 rounded">
                <option value="">Select Value</option>
                {sourceOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
              </select>
            )
          }
          if (key === 'Country') {
            return (
              <select value={value} onChange={e => setValue(e.target.value)} className="w-full border px-3 py-2 rounded">
                <option value="">Select Country</option>
                {countryOptions.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            )
          }
          if (key === 'State') {
            return (
              <div className="space-y-2">
                <select value={selectedCountry} onChange={e => { setSelectedCountry(e.target.value); setValue(''); setSelectedState(''); fetchStates(e.target.value) }} className="w-full border px-3 py-2 rounded">
                  <option value="">Select Country</option>
                  {countryOptions.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
                <select value={value} onChange={e => setValue(e.target.value)} className="w-full border px-3 py-2 rounded">
                  <option value="">Select State</option>
                  {stateOptions.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
              </div>
            )
          }
          if (key === 'City') {
            return (
              <div className="space-y-2">
                <select value={selectedCountry} onChange={e => { setSelectedCountry(e.target.value); setSelectedState(''); setValue(''); fetchStates(e.target.value) }} className="w-full border px-3 py-2 rounded">
                  <option value="">Select Country</option>
                  {countryOptions.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
                <select value={selectedState} onChange={e => { setSelectedState(e.target.value); setValue(''); fetchCities(e.target.value) }} className="w-full border px-3 py-2 rounded">
                  <option value="">Select State</option>
                  {stateOptions.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
                <select value={value} onChange={e => setValue(e.target.value)} className="w-full border px-3 py-2 rounded">
                  <option value="">Select City</option>
                  {cityOptions.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </div>
            )
          }
          return <input value={value} onChange={e => setValue(e.target.value)} placeholder="Enter field value" className="w-full border px-3 py-2 rounded" />
        })()}
      </div>

      {/* add button */}
      <div className="flex justify-end mb-4">
        <button onClick={handleAdd} className="inline-flex items-center gap-2 px-3 py-2 bg-blue-500 text-white rounded">
          <span className="text-xl">+</span> Add
        </button>
      </div>

      {/* entries table */}
      <div className="mb-4 border-t pt-3">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-gray-700">
              <th className="py-2">Field Name</th>
              <th className="py-2">Field Value</th>
              <th className="py-2">&nbsp;</th>
            </tr>
          </thead>
          <tbody>
            {entries.length === 0 ? (
              <tr><td colSpan={3} className="py-4 text-gray-500">No fields added</td></tr>
            ) : entries.map((e, idx) => (
              <tr key={e.field} className="border-t">
                <td className="py-3">{(defaultFieldOptions.find(f => String(f.key) === String(e.field))?.label) || e.field}</td>
                <td className="py-3">{e.label || e.value}</td>
                <td className="py-3 text-right">
                  <button onClick={() => handleRemove(idx)} className="px-2 py-1 bg-gray-800 text-white rounded">-</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* schedule */}
      <div className="mt-2">
        <label className="inline-flex items-center mr-4">
          <input type="checkbox" checked={schedule} onChange={() => setSchedule(s => !s)} className="rounded-[3px] mr-2" />
          On Schedule Date And Time
        </label>
      </div>
      {schedule && (
        <div className="mt-3">
          <input type="datetime-local" value={scheduleAt} onChange={e => setScheduleAt(e.target.value)} className="w-full border px-3 py-2 rounded" />
        </div>
      )}

      {error && <div className="text-red-600 text-sm mt-3">{error}</div>}



    </div>
  )
}

export default UpdateForm
